import React, { useState } from "react";
import { Upload, FileText, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Premium file uploader. Returns array of { id, path, filename }.
 * onChange receives the up-to-date list.
 */
export default function FileUpload({ category = "general", value = [], onChange, accept = "application/pdf,image/*", testid = "file-upload" }) {
    const [busy, setBusy] = useState(false);

    const handle = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setBusy(true);
        const next = [...value];
        try {
            for (const f of files) {
                if (f.size > 10 * 1024 * 1024) {
                    toast.error(`${f.name} is larger than 10MB`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", f);
                fd.append("category", category);
                const { data } = await api.post("/uploads", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                next.push(data);
            }
            onChange?.(next);
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            e.target.value = "";
        }
    };

    const remove = (idx) => {
        const next = value.filter((_, i) => i !== idx);
        onChange?.(next);
    };

    return (
        <div className="space-y-3" data-testid={testid}>
            <label className="flex items-center justify-center gap-3 border border-dashed border-white/15 hover:border-saffron transition-colors p-6 cursor-pointer text-white/65 text-sm">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                <span>{busy ? "Uploading..." : "Click to upload PDF or image (max 10MB)"}</span>
                <input
                    type="file"
                    accept={accept}
                    multiple
                    className="hidden"
                    onChange={handle}
                    disabled={busy}
                    data-testid={`${testid}-input`}
                />
            </label>
            {value.length > 0 && (
                <ul className="space-y-2">
                    {value.map((f, i) => (
                        <li
                            key={f.id}
                            className="flex items-center justify-between border border-white/8 px-3 py-2 text-sm text-white/80"
                        >
                            <span className="flex items-center gap-2 truncate">
                                <FileText size={14} className="text-saffron flex-shrink-0" />
                                <span className="truncate">{f.filename}</span>
                            </span>
                            <button
                                type="button"
                                onClick={() => remove(i)}
                                className="text-white/45 hover:text-white"
                                aria-label="Remove"
                                data-testid={`${testid}-remove-${i}`}
                            >
                                <X size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
